/**
 * IraDisclosureBodiesPage — Documents & Disclosures · Disclosure Bodies.
 *
 * Lists every DisclosureTemplate with its inline body status (placeholder /
 * authored / version). Each row inlines a textarea for the verbatim body the
 * disclosure-engine renders; saving bumps the body version and clears the
 * placeholder flag.
 *
 * Contract:
 *   - Read:  client.retail.ira.disclosureBodies.list({ layer, includeInactive })
 *            → GET /v3/ira/disclosure-bodies
 *   - Write: client.retail.ira.disclosureBodies.saveBody(SENTINEL, code, { disclosureBody })
 *            → PUT /v3/ira/disclosure-bodies/{code}
 *
 * Migrated 2026-06-16 from ROP-teams Admin (operational app writes no config).
 * Auth: ADMIN_AUTH_TOKEN_SENTINEL (MSAL bearer; no secret in the bundle).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ADMIN_AUTH_TOKEN_SENTINEL } from '@retail-ops/api-client';
import type { DisclosureTemplateBodyDto } from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { TextArea, SelectField } from '@/components/FormField';

interface DraftRow extends DisclosureTemplateBodyDto {
  draftBody: string;
  dirty: boolean;
}

const STATUS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'all', label: 'All templates' },
  { value: 'placeholder', label: 'Placeholder only (needs author)' },
  { value: 'authored', label: 'Authored' },
];

const LAYER_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'All layers' },
  { value: 'universal', label: 'Universal' },
  { value: 'product', label: 'Product' },
  { value: 'entity', label: 'Entity' },
  { value: 'ownership', label: 'Ownership' },
  { value: 'conditional', label: 'Conditional' },
];

export function IraDisclosureBodiesPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<DraftRow> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [savingFor, setSavingFor] = useState<string | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [layerFilter, setLayerFilter] = useState('');
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setRows(null);
      setLoadError(null);
      try {
        const resp = await client.retail.ira.disclosureBodies.list({
          layer: layerFilter || undefined,
          includeInactive: false,
        });
        if (cancelled) return;
        const fetched = resp.templates ?? [];
        setRows(fetched.map((t) => ({ ...t, draftBody: t.disclosureBody ?? '', dirty: false })));
      } catch (e) {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : 'Failed to load templates.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, layerFilter, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const updateDraft = useCallback((code: string, body: string) => {
    setRows((prev) =>
      prev
        ? prev.map((r) =>
            r.code === code ? { ...r, draftBody: body, dirty: body !== (r.disclosureBody ?? '') } : r,
          )
        : prev,
    );
  }, []);

  const save = useCallback(
    async (code: string) => {
      const row = rows?.find((r) => r.code === code);
      if (!row || !row.dirty) return;
      setSavingFor(code);
      try {
        const resp = await client.retail.ira.disclosureBodies.saveBody(ADMIN_AUTH_TOKEN_SENTINEL, code, {
          disclosureBody: row.draftBody,
        });
        if (!resp.success) {
          setToast({ tone: 'warn', text: resp.error ?? 'Save failed' });
          return;
        }
        setToast({ tone: 'info', text: `Saved ${code} (v${resp.disclosureBodyVersion ?? '?'}).` });
        refresh();
      } catch (e) {
        setToast({ tone: 'warn', text: e instanceof Error ? e.message : 'Save failed' });
      } finally {
        setSavingFor(null);
      }
    },
    [client, rows, refresh],
  );

  const filtered = useMemo(() => {
    if (!rows) return rows;
    if (statusFilter === 'placeholder') return rows.filter((r) => r.disclosureBodyIsPlaceholder);
    if (statusFilter === 'authored') return rows.filter((r) => !r.disclosureBodyIsPlaceholder);
    return rows;
  }, [rows, statusFilter]);

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load disclosure bodies: {loadError} <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Card title="Disclosure Bodies">
        <div className="filter-grid">
          <SelectField label="Status" value={statusFilter} onChange={setStatusFilter} options={STATUS_OPTIONS} />
          <SelectField label="Layer" value={layerFilter} onChange={setLayerFilter} options={LAYER_OPTIONS} />
        </div>
        <p className="cell-desc">
          Inline verbatim disclosure text rendered by the disclosure-engine. Saving a row bumps its
          version and clears the placeholder flag.
        </p>
      </Card>

      {!rows ? (
        <Spinner label="Loading disclosure bodies…" />
      ) : (filtered?.length ?? 0) === 0 ? (
        <Card title="">
          <p className="empty">No templates match the current filter.</p>
        </Card>
      ) : (
        (filtered ?? []).map((r) => (
          <Card
            key={r.code}
            title={r.displayName}
            actions={
              r.disclosureBodyIsPlaceholder ? (
                <Pill tone="warn">placeholder</Pill>
              ) : (
                <Pill tone="ok">authored v{r.disclosureBodyVersion}</Pill>
              )
            }
          >
            <div className="form-stack">
              <p className="cell-desc">
                <code className="cell-mono">{r.code}</code> · Layer: {r.layer} · Audience: {r.audience}
              </p>
              <TextArea
                label="Disclosure body (verbatim HTML)"
                value={r.draftBody}
                onChange={(v) => updateDraft(r.code, v)}
                rows={6}
                placeholder="Paste verbatim disclosure body HTML here…"
              />
              <div className="card-actions-row">
                <Button
                  variant="primary"
                  onClick={() => void save(r.code)}
                  disabled={!r.dirty || savingFor === r.code}
                >
                  {savingFor === r.code ? 'Saving…' : 'Save'}
                </Button>
                {r.disclosureBodyAuthoredBy && !r.disclosureBodyIsPlaceholder && (
                  <span className="cell-desc">
                    Last authored by {r.disclosureBodyAuthoredBy}
                    {r.disclosureBodyAuthoredAt
                      ? ` on ${new Date(r.disclosureBodyAuthoredAt).toLocaleString()}`
                      : ''}
                  </span>
                )}
              </div>
            </div>
          </Card>
        ))
      )}
    </>
  );
}
